import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { Send } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { makeRESTApiRequests } from "../helpers/api";
import { ENDPOINTS, INQUIRY_MAX_HISTORY_MESSAGES } from "../helpers/constants";

const normalizeInquiryToken = (value) =>
  typeof value === "string" ? value.trim().toUpperCase() : "";

const SUPPORT_EMAIL_OFFER =
  "I am unable to answer that question based on the survey records. Would you like me to send a support request to our team to assist you?";

const SUPPORT_EMAIL_YES = "Yes, please";
const SUPPORT_EMAIL_NO = "No, thanks";

const Inquire = () => {
  const { token: routeToken } = useParams();
  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState(routeToken || "");
  const [token, setToken] = useState(routeToken || "");
  const [validating, setValidating] = useState(Boolean(routeToken));
  const [jobId, setJobId] = useState(null);
  const [chatAvailable, setChatAvailable] = useState(false);
  const [closedMessage, setClosedMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (routeToken) {
      const normalized = normalizeInquiryToken(routeToken);
      setToken(normalized);
      setTokenInput(normalized);
      validateToken(normalized);
    }
  }, [routeToken]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const validateToken = async (inquiryToken) => {
    setValidating(true);
    setJobId(null);
    setMessages([]);
    setClosedMessage("");
    setChatAvailable(false);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.INQUIRY_VALIDATE,
      requestBody: { token: inquiryToken },
    });

    setValidating(false);

    if (error) {
      toast.error(error);
      return false;
    }

    setJobId(response.jobId || null);
    setChatAvailable(Boolean(response.chatAvailable));
    setClosedMessage(response.closedMessage || "");

    if (response.chatAvailable) {
      setMessages([
        {
          role: "assistant",
          text: "Hi! You can ask about the status, schedule, or report progress for this survey request.",
        },
      ]);
    }

    return true;
  };

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    const normalized = normalizeInquiryToken(tokenInput);
    if (!normalized) {
      toast.error("Please enter your inquiry token.");
      return;
    }

    const ok = await validateToken(normalized);
    if (ok) {
      setToken(normalized);
      if (normalizeInquiryToken(routeToken) !== normalized) {
        navigate(`/inquire/${normalized}`, { replace: true });
      }
    }
  };

  const sendMessage = async (preset) => {
    const message = (preset ?? draft).trim();
    if (!message || sending || !chatAvailable) return;

    if (preset == null) setDraft("");
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setSending(true);

    const history = messages
      .map(({ role, text }) => ({ role, text }))
      .slice(-INQUIRY_MAX_HISTORY_MESSAGES);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.INQUIRY_CHAT,
      requestBody: { token, message, history },
    });

    setSending(false);

    if (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: error,
        },
      ]);
      if (
        error.includes("completed") ||
        error.includes("cancelled") ||
        error.includes("closed")
      ) {
        setChatAvailable(false);
        setClosedMessage(error);
      }
      return;
    }

    setMessages((prev) => [
      ...prev,
      { role: "assistant", text: response.reply },
    ]);
  };

  const handleSend = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const lastMessage = messages[messages.length - 1];
  const showSupportChoice =
    !sending &&
    lastMessage?.role === "assistant" &&
    lastMessage.text.trim() === SUPPORT_EMAIL_OFFER;

  if (validating) {
    return (
      <Container className="py-5">
        <Loader />
      </Container>
    );
  }

  if (!token || !jobId) {
    return (
      <Container className="py-4" style={{ maxWidth: 520 }}>
        <h4 className="mb-2 text-dark">Survey Inquiry</h4>
        <p className="text-muted small mb-4">
          Enter the inquiry token from your confirmation email to check your
          survey request status.
        </p>
        <Form onSubmit={handleTokenSubmit}>
          <Form.Group>
            <Form.Label className="small">Inquiry token</Form.Label>
            <Form.Control
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="e.g. ABC12XYZ"
              maxLength={8}
              autoFocus
            />
          </Form.Group>
          <Button type="submit" variant="primary" className="mt-3">
            Continue
          </Button>
        </Form>
      </Container>
    );
  }

  return (
    <Container className="py-3" style={{ maxWidth: 720 }}>
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1 text-dark">Survey Inquiry</h4>
        </Col>
      </Row>

      {!chatAvailable ? (
        <Alert variant="secondary" className="mb-0">
          {closedMessage ||
            "This survey request chat is no longer available."}
        </Alert>
      ) : (
        <>
          <div
            className="border rounded p-3 mb-3 bg-light"
            style={{ minHeight: 320, maxHeight: 480, overflowY: "auto" }}
          >
            {messages.map((msg, index) => (
              <div
                key={`${msg.role}-${index}`}
                className={`d-flex mb-3 ${
                  msg.role === "user"
                    ? "justify-content-end"
                    : "justify-content-start"
                }`}
              >
                <div
                  className={`px-3 py-2 small ${
                    msg.role === "user"
                      ? "bg-primary text-white"
                      : "bg-white border text-dark"
                  }`}
                  style={{
                    maxWidth: "80%",
                    borderRadius: 12,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {showSupportChoice && (
              <div className="d-flex flex-wrap mb-2">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  className="mr-2 mb-2"
                  onClick={() => sendMessage(SUPPORT_EMAIL_YES)}
                >
                  {SUPPORT_EMAIL_YES}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  className="mb-2"
                  onClick={() => sendMessage(SUPPORT_EMAIL_NO)}
                >
                  {SUPPORT_EMAIL_NO}
                </Button>
              </div>
            )}
            {sending && (
              <div className="text-muted small">Thinking...</div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <Form onSubmit={handleSend}>
            <div className="d-flex">
              <Form.Control
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask about status, schedule, or report progress..."
                disabled={sending}
                maxLength={500}
              />
              <Button
                type="submit"
                variant="primary"
                className="ml-2"
                disabled={sending || !draft.trim()}
              >
                <Send />
              </Button>
            </div>
          </Form>
        </>
      )}
    </Container>
  );
};

export default Inquire;
